import { Controller } from 'stimulus'
import {
  colors, days1, empty, esc, fmtDay, leadTrend, niceTicks, peopleBars, placeTip, sparkline, spanBins, statusBar
} from '../lib/shared_dashboard/charts'

// Shared dashboard page (/d/<token>, SharedDashboards::DashboardsController).
// One JSON request per date range / status pair fills every card; the open
// jobs table sorts client-side.
const DAY = 864e5
const isoAdd = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10)
const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY)
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`
const WARN = '<svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="currentColor"/>'
  + '<rect x="7.1" y="3.6" width="1.8" height="5.6" rx=".9" fill="white"/><circle cx="8" cy="11.6" r="1.05" fill="white"/></svg>'

export default class extends Controller {
  static targets = [
    'from', 'to', 'preset', 'updated', 'stats', 'body', 'trend', 'pairFrom', 'pairTo', 'pairFig', 'pairChart',
    'statusTime', 'byAssignee', 'byRequester', 'requesterPanel', 'openTitle', 'openSub', 'openRows', 'sort', 'tip'
  ];

  connect() {
    this.today = this.data.get('today')
    this.range = { from: this.data.get('from'), to: this.data.get('to') }
    this.pair = {}
    this.sortKey = 'due'
    this.tips = []
    this.fromTarget.max = this.today
    this.toTarget.max = this.today
    this.syncInputs()

    this.onMove = e => this.moveTip(e)
    this.onResize = () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => this.render(), 150) }
    document.addEventListener('pointermove', this.onMove)
    window.addEventListener('resize', this.onResize)
    this.load()
  }

  disconnect() {
    document.removeEventListener('pointermove', this.onMove)
    window.removeEventListener('resize', this.onResize)
  }

  // ---- inputs ----

  preset(event) {
    const { days } = event.currentTarget.dataset
    this.range.to = this.today
    this.range.from = days === 'ytd' ? `${this.today.slice(0, 4)}-01-01` : isoAdd(this.today, -(Number(days) - 1))
    this.presetTargets.forEach(b => b.classList.toggle('is-on', b === event.currentTarget))
    this.syncInputs()
    this.load()
  }

  datesChanged() {
    let from = this.fromTarget.value; let to = this.toTarget.value
    if (!from || !to) return
    if (from > to) [from, to] = [to, from]
    this.range = { from, to }
    this.presetTargets.forEach(b => b.classList.remove('is-on'))
    this.syncInputs()
    this.load()
  }

  pairChanged() {
    this.pair = { from: this.pairFromTarget.value, to: this.pairToTarget.value }
    this.load()
  }

  sortOpen(event) {
    this.sortKey = event.currentTarget.dataset.sort
    this.sortTargets.forEach(b => b.classList.toggle('is-on', b === event.currentTarget))
    this.renderOpen()
  }

  syncInputs() {
    this.fromTarget.value = this.range.from
    this.toTarget.value = this.range.to
  }

  // ---- data ----

  async load() {
    const params = new URLSearchParams({ from: this.range.from, to: this.range.to })
    if (this.pair.from) params.set('from_status', this.pair.from)
    if (this.pair.to) params.set('to_status', this.pair.to)
    const seq = (this.seq || 0) + 1
    this.seq = seq
    this.bodyTarget.classList.add('sd-loading')
    this.statsTarget.classList.add('sd-loading')
    try {
      const res = await fetch(`${this.data.get('url')}?${params}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.status === 401) { window.location.reload(); return }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const payload = await res.json()
      if (seq !== this.seq) return
      this.payload = payload
      this.loadedAt = new Date()
      this.render()
    } catch (err) {
      if (seq === this.seq) this.updatedTarget.textContent = "Couldn't load the dashboard. Refresh the page to try again."
    } finally {
      if (seq === this.seq) {
        this.bodyTarget.classList.remove('sd-loading')
        this.statsTarget.classList.remove('sd-loading')
      }
    }
  }

  // ---- rendering ----

  render() {
    if (!this.payload) return
    this.tips = []
    this.c = colors(this.element)
    this.updatedTarget.textContent = `Updated ${this.loadedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Draft jobs aren't counted.`
    this.renderStats()
    this.renderTrend()
    this.renderPair()
    this.renderStatusTime()
    this.renderPeople()
    this.renderOpen()
  }

  tip(html) {
    this.tips.push(html)
    return `data-sd-tip="${this.tips.length - 1}"`
  }

  get tipFn() {
    return html => this.tip(html)
  }

  renderStats() {
    const { kpis, lead_trend: trend, range, open_summary: open } = this.payload
    const cur = kpis.current; const prev = kpis.previous; const len = range.days
    // More completions is good news; new jobs are demand, neither good nor bad.
    const pct = (a, b, judged) => {
      if (!b) return '<span class="sd-delta">No jobs in the prior period</span>'
      const p = Math.round(((a - b) / b) * 100)
      if (p === 0) return `<span class="sd-delta">Same as the prior ${len} days</span>`
      const tone = judged ? ` class="${p > 0 ? 'sd-good' : 'sd-bad'}"` : ''
      return `<span class="sd-delta"><b${tone}>${p > 0 ? '&#9650;' : '&#9660;'} ${Math.abs(p)}%</b> vs the prior ${len} days</span>`
    }
    let leadDelta = '<span class="sd-delta">No completions in the prior period</span>'
    if (cur.median_lead_days != null && prev.median_lead_days != null) {
      const diff = cur.median_lead_days - prev.median_lead_days
      leadDelta = Math.abs(diff) < 0.05
        ? `<span class="sd-delta">Same as the prior ${len} days</span>`
        : `<span class="sd-delta"><b class="${diff < 0 ? 'sd-good' : 'sd-bad'}">${diff < 0 ? '&#9660;' : '&#9650;'} ${Math.abs(diff).toFixed(1)} days ${diff < 0 ? 'faster' : 'slower'}</b> than the prior ${len} days</span>`
    }
    const { mark } = this.c
    this.statsTarget.innerHTML = `
      <div class="sd-stat"><span class="sd-stat-lab">Jobs completed</span><div class="sd-stat-row"><span class="sd-stat-val">${cur.completed}</span>${sparkline(kpis.spark.completed, mark)}</div>${pct(cur.completed, prev.completed, true)}</div>
      <div class="sd-stat"><span class="sd-stat-lab">Median lead time</span><div class="sd-stat-row"><span class="sd-stat-val">${days1(cur.median_lead_days)}${cur.median_lead_days == null ? '' : '<small>days</small>'}</span>${sparkline(trend.map(w => w.median_lead_days), mark)}</div>${leadDelta}</div>
      <div class="sd-stat"><span class="sd-stat-lab">New jobs</span><div class="sd-stat-row"><span class="sd-stat-val">${cur.created}</span>${sparkline(kpis.spark.created, mark)}</div>${pct(cur.created, prev.created, false)}</div>
      <a class="sd-stat" href="#open-jobs"><span class="sd-stat-lab">Open now</span><div class="sd-stat-row"><span class="sd-stat-val">${open.total}</span></div><span class="sd-delta">${open.late ? `<b class="sd-bad">${plural(open.late, 'job')} past due</b>` : 'None past due'}</span></a>`
  }

  renderTrend() {
    const weeks = this.payload.lead_trend
    if (!weeks.some(w => w.completed)) { empty(this.trendTarget, 'No jobs were completed in this range.'); return }
    leadTrend(this.trendTarget, weeks, this.c, this.tipFn)
  }

  renderPair() {
    const { statuses, status_pair: pair } = this.payload
    if (!pair) {
      this.pairFromTarget.innerHTML = ''; this.pairToTarget.innerHTML = ''
      this.pairFigTarget.innerHTML = ''
      empty(this.pairChartTarget, 'Needs at least two job statuses.')
      return
    }
    const opts = (list, selected) => list.map(s => `<option value="${s.id}"${s.id === selected ? ' selected' : ''}>${esc(s.name)}</option>`).join('')
    const fromIdx = statuses.findIndex(s => s.id === pair.from_id)
    this.pairFromTarget.innerHTML = opts(statuses.slice(0, -1), pair.from_id)
    this.pairToTarget.innerHTML = opts(statuses.slice(fromIdx + 1), pair.to_id)
    this.pair = { from: String(pair.from_id), to: String(pair.to_id) }
    if (!pair.jobs) {
      this.pairFigTarget.innerHTML = ''
      empty(this.pairChartTarget, 'No jobs reached the second status in this range.')
      return
    }
    const waiting = pair.waiting ? `<br>${plural(pair.waiting, 'job')} still waiting` : ''
    this.pairFigTarget.innerHTML = `<span class="sd-big">${days1(pair.median_days)}<small>days</small></span>`
      + `<span class="sd-ctx">median across ${plural(pair.jobs, 'job')}<br>${pair.within_3_pct}% within 3 days${waiting}</span>`
    spanBins(this.pairChartTarget, pair.bins, pair.median_days, this.c, this.tipFn)
  }

  renderStatusTime() {
    const rows = this.payload.status_time.filter(r => r.median_days != null)
    const el = this.statusTimeTarget
    if (!rows.length) { empty(el, 'No status changes for jobs created in this range.'); return }
    const ticks = niceTicks(Math.max(...rows.map(r => r.median_days)), 4)
    const xMax = ticks[ticks.length - 1]
    el.innerHTML = rows.map((r, i) => `<div class="sd-st-row"><div class="sd-st-name">${esc(r.status)}<span>${plural(r.jobs, 'job')}</span></div><div class="sd-chart" data-row="${i}"></div></div>`).join('')
    rows.forEach((r, i) => statusBar(el.querySelector(`[data-row="${i}"]`), r, xMax, ticks, this.c, this.tipFn))
  }

  renderPeople() {
    const { by_assignee: assignees, by_requester: requesters } = this.payload
    if (assignees.length) peopleBars(this.byAssigneeTarget, assignees, 8, 'Jobs by assignee', this.c, this.tipFn)
    else empty(this.byAssigneeTarget, 'No new jobs in this range.')
    this.requesterPanelTarget.hidden = requesters == null
    if (requesters == null) return
    if (requesters.length) peopleBars(this.byRequesterTarget, requesters, 7, 'Jobs by requester', this.c, this.tipFn)
    else empty(this.byRequesterTarget, 'No new jobs in this range.')
  }

  renderOpen() {
    if (!this.payload) return
    const comments = this.data.get('comments') === 'true'
    const requesters = this.data.get('requesters') === 'true'
    const jobs = [...this.payload.open_jobs]
    if (this.sortKey === 'submitted') jobs.sort((a, b) => a.submitted_at.localeCompare(b.submitted_at))
    const { total, late, limit } = this.payload.open_summary
    const shown = total > jobs.length ? `Showing the ${limit} due soonest. ` : ''
    this.openTitleTarget.textContent = `Open jobs (${total})`
    this.openSubTarget.textContent = `${late ? `${plural(late, 'job')} past due. ` : ''}${shown}Every job not yet completed. The date range doesn't apply here.`
    if (!jobs.length) {
      this.openRowsTarget.innerHTML = `<tr><td colspan="${5 + (comments ? 1 : 0) + (requesters ? 1 : 0)}" class="sd-none">No open jobs.</td></tr>`
      return
    }
    const now = Date.now()
    const ago = (iso) => {
      const h = (now - Date.parse(iso)) / 36e5
      if (h < 1) return 'just now'
      if (Math.round(h) < 24) return `${Math.round(h)} hour${Math.round(h) === 1 ? '' : 's'} ago`
      const d = Math.round(h / 24)
      return d === 1 ? 'yesterday' : `${d} days ago`
    }
    this.openRowsTarget.innerHTML = jobs.map((j) => {
      let due = '<span class="sd-none">Not set</span>'
      if (j.due) {
        const diff = daysBetween(this.today, j.due)
        const kind = j.due_kind === 'requested' ? 'Requested completion' : 'Estimated completion'
        let sub = `in ${plural(diff, 'day')}`
        if (j.late_days) sub = `<span class="sd-late">${WARN}${plural(j.late_days, 'day')} late</span>`
        else if (diff === 0) sub = 'today'
        due = `<span title="${kind}">${fmtDay(j.due)}</span><span class="sd-cell-sub">${sub}</span>`
      }
      let comment = ''
      if (comments) {
        comment = j.comment
          ? `<td class="sd-cmt"><p>${esc(j.comment.text)}</p><span class="sd-cell-sub">${esc(j.comment.by || 'Someone')} · ${ago(j.comment.at)}</span></td>`
          : '<td class="sd-cmt"><span class="sd-none">No comments yet</span></td>'
      }
      return `<tr>
        <td class="sd-job"><b>#${esc(j.number)}</b>${esc(j.name || '')}</td>
        <td><span class="sd-pill${j.in_progress ? ' is-active' : ''}">${esc(j.status || 'No status')}</span></td>
        <td class="sd-num">${fmtDay(j.submitted_at)}<span class="sd-cell-sub">${ago(j.submitted_at)}</span></td>
        <td class="sd-num">${due}</td>
        <td>${esc(j.assignees.join(', ')) || '<span class="sd-none">None</span>'}</td>
        ${requesters ? `<td>${esc(j.requesters.join(', ')) || '<span class="sd-none">None</span>'}</td>` : ''}
        ${comment}
      </tr>`
    }).join('')
  }

  // ---- tooltip ----

  moveTip(e) {
    placeTip(e, this.element, this.tipTarget, this.tips)
  }
}
